import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useEffect, useRef, useState } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import { MdCheck, MdClose, MdFileUpload, MdInfo } from "react-icons/md";
import "./FileUpload.css";

import { CPM_MAX_FILE_UPLOAD_SIZE } from "../../shared/constants/FeatureToggleKeys";
import {
    ALLOWED_FILE_TYPES_DISPLAY,
    ALLOWED_MIME_TYPES,
    DEFAULT_MAX_FILE_SIZE_MB,
    LARGE_FILE_WARNING_MB,
} from "../../shared/constants/FileUploadConstants";
import useFeatureFlags from "../../shared/services/featureToggle/useFeatureFlags";

const SupportedTypesContent = () => (
  <div className="file-upload-tooltip-content">
    <strong>Supported file types:</strong>
    <ul className="file-upload-tooltip-list">
      {ALLOWED_FILE_TYPES_DISPLAY.map((type) => (
        <li key={type}>{type}</li>
      ))}
    </ul>
  </div>
);

/**
 * FileUpload — Shared reusable file selection and validation component.
 *
 * Drop-in replacement for FileDrop with:
 * - File size enforcement via CPM_MAX_FILE_UPLOAD_SIZE feature flag (default 400 MB)
 * - Info icon tooltip showing allowed file types
 * - Zip files excluded from allowed types
 *
 * Props (identical to FileDrop):
 * @param {Function} onCancel        - Called when Close is clicked; defaults to window.location.reload
 * @param {boolean}  multiple        - Allow multiple file selection
 * @param {Function} onUpload        - Called by retry logic: (fileName, validFiles)
 * @param {Function} onFilesAdded    - Called when user clicks Continue: (validFilesArray)
 * @param {boolean}  disableContinue - Disables Continue even when valid files are present
 */
export function FileUpload({ onCancel, multiple, onUpload, onFilesAdded, disableContinue }) {
  const fileInputRef = useRef();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validFiles, setValidFiles] = useState([]);
  const [unsupportedFiles, setUnsupportedFiles] = useState([]);
  const [disableSaveAndExit, setDisableSaveAndExit] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [retryFiles, setRetryFiles] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Feature flag: runtime-configurable max file upload size
  const { getFeatureValue } = useFeatureFlags([CPM_MAX_FILE_UPLOAD_SIZE]);
  const rawValue = getFeatureValue(CPM_MAX_FILE_UPLOAD_SIZE, '0');
  const parsed = parseInt(rawValue, 10);
  const MAX_FILE_SIZE_MB = (parsed > 0) ? parsed : DEFAULT_MAX_FILE_SIZE_MB;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const addRetryFiles = (file) => {
    !retryFiles.includes(file) && setRetryFiles([...retryFiles, file]);
  };

  // Deduplicate and enforce single-file mode
  useEffect(() => {
    let filteredArr = selectedFiles.reduce((acc, current) => {
      const x = acc.find((item) => item.name === current.name);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    if (filteredArr.length && !multiple) {
      filteredArr = filteredArr.slice(-1);
    }

    setValidFiles([...filteredArr]);
  }, [selectedFiles, multiple]);

  const dragOver = (e) => { e.preventDefault(); };
  const dragEnter = (e) => { e.preventDefault(); };
  const dragLeave = (e) => { e.preventDefault(); };

  const close = onCancel || (() => window.location.reload(false));

  const fileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFiles(files);
    }
  };

  const filesSelected = () => {
    setRetryFiles([]);
    if (fileInputRef.current.files.length) {
      handleFiles(fileInputRef.current.files);
    }
  };

  const fileInputClicked = () => {
    fileInputRef.current.click();
  };

  const handleFiles = (files) => {
    setDisableSaveAndExit(false);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isValidType(file)) {
        file.invalid = true;
        setSelectedFiles((prev) => [...prev, file]);
        setUnsupportedFiles((prev) => [...prev, file]);
      } else if (isOversized(file)) {
        file.invalid = true;
        file.oversized = true;
        setSelectedFiles((prev) => [...prev, file]);
        setUnsupportedFiles((prev) => [...prev, file]);
      } else {
        // Show large-file snackbar advisory (file is still accepted)
        if (!showSnackbar && file.size > LARGE_FILE_WARNING_MB * 1024 * 1024) {
          setShowSnackbar(true);
        }
        setSelectedFiles((prev) => [...prev, file]);
      }
    }
  };

  const isValidType = (file) => {
    // Reject filenames over 128 characters
    if (file.name?.length > 128) return false;
    // Accept if MIME is in the allowlist
    if (ALLOWED_MIME_TYPES.indexOf(file.type) !== -1) return true;
    // Special case: .msg files with octet-stream MIME (not already caught above)
    if (file.name?.endsWith(".msg") && file.name.length < 128) return true;
    return false;
  };

  const isOversized = (file) => {
    return file.size > MAX_FILE_SIZE_BYTES;
  };

  const fileType = (fileName) => {
    return (
      fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) ||
      fileName
    );
  };

  const removeFile = (name) => {
    setValidFiles((prev) => prev.filter((f) => f.name !== name));
    setSelectedFiles((prev) => prev.filter((f) => f.name !== name));
    setUnsupportedFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const uploadFiles = async (fileName) => {
    setDisableSaveAndExit(true);
    if (fileName != null) {
      setRetryFiles(retryFiles.filter((x) => x !== fileName));
      onUpload?.(fileName, validFiles);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason !== 'clickaway') {
      setShowSnackbar(false);
    }
  };

  const handleFilesAdded = (event) => {
    event.preventDefault();
    onFilesAdded?.(validFiles);
  };

  const errorMessage = () => {
    if (unsupportedFiles.length > 0) {
      const hasOversized = unsupportedFiles.some((f) => f.oversized);
      if (hasOversized) {
        return 'File size exceeds the maximum allowed limit of '+ MAX_FILE_SIZE_MB +' MB. Please consider splitting the file to upload.'; 
      }
      return "Invalid File Format; Check type, size, or name length.";
    }
    return "";
  };

  const displayStatus = (data) => {
    const containRetry = retryFiles.includes(data.name);

    switch (data.status) {
      case "init":
        return (
          <>
            <div className="file-remove"> </div>
            <div className="progress-container">
              <ProgressBar now={data.percentage} />
              <p className="file-status">{data.displayStatus}</p>
            </div>
          </>
        );
      case "completed":
        return (
          <>
            <div className="file-done"><MdCheck /></div>
            <div className="progress-container">
              <ProgressBar variant="success" now={data.percentage} />
            </div>
          </>
        );
      case "error":
        return (
          <>
            <div className="file-remove">
              {!containRetry ? (
                <>
                  Error{" "}
                  <Button
                    style={{ height: "unset", width: "unset", padding: "unset" }}
                    onClick={(e) => {
                      e.preventDefault();
                      addRetryFiles(data.name);
                      uploadFiles(data.name);
                    }}
                  >
                    Retry
                  </Button>
                </>
              ) : (<></>)}
            </div>
            <div className="progress-container">
              {containRetry
                ? <ProgressBar now={0} />
                : <ProgressBar variant="danger" now={data.percentage} />}
            </div>
          </>
        );
      default:
        return (
          <div className="file-remove" onClick={() => removeFile(data.name)}>
            <MdClose />
          </div>
        );
    }
  };

  return (
    <div className="content">
      <form>
        <div className="content-container">
          <div className="file-upload-title-row">
            <span className="content-title">Attach Documents</span>
            <Tooltip
              title={<SupportedTypesContent />}
              arrow
              open={tooltipOpen}
              onOpen={() => setTooltipOpen(true)}
              onClose={() => setTooltipOpen(false)}
              interactive
            >
              <span
                className="file-upload-info-icon"
                data-testid="info-icon"
                onClick={() => setTooltipOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setTooltipOpen((prev) => !prev);
                  }
                }}
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
                role="button"
                tabIndex={0}
                aria-label="Show supported file types"
              >
                <MdInfo />
              </span>
            </Tooltip>
          </div>

          {!validFiles.length || multiple ? (
            <div
              className="drop-container"
              onDragOver={dragOver}
              onDragEnter={dragEnter}
              onDragLeave={dragLeave}
              onDrop={fileDrop}
              onClick={fileInputClicked}
            >
              <div className="drop-message">
                <div className="upload-icon">
                  <MdFileUpload className="upload-icon-md" />
                </div>
                <div className="upload-text">Drag and Drop here</div>
                <div className="upload-text">or</div>
                <div className="upload-text-button">Browse Files</div>
              </div>
              <input
                ref={fileInputRef}
                className="file-input"
                type="file"
                multiple={multiple || false}
                onChange={filesSelected}
              />
            </div>
          ) : null}

          <div className="file-display-container">
            {validFiles.map((data, i) => (
              <div className="file-status-bar" key={i}>
                <div>
                  <div className="file-type-logo"></div>
                  <div className="file-type">{fileType(data.name)}</div>
                  <span className={`file-name ${data.invalid ? "file-error" : ""}`}>
                    {data.name.length > 30
                      ? data.name.substring(0, 25) + "..."
                      : data.name}
                  </span>
                </div>
                {displayStatus(data)}
              </div>
            ))}
          </div>
        </div>

        <div className="upload-button-container">
          <Button className="close-button" onClick={close}>
            Close
          </Button>

          {unsupportedFiles.length ? (
            <p className="error">{errorMessage()}</p>
          ) : (
            <Button
              className="upload-button"
              type="button"
              onClick={handleFilesAdded}
              disabled={!validFiles.length || disableSaveAndExit || disableContinue}
            >
              Continue
            </Button>
          )}
        </div>
      </form>

      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
          Files &gt;{LARGE_FILE_WARNING_MB} MB will take longer than 8 minutes to upload.
        </Alert>
      </Snackbar>
    </div>
  );
}

export default FileUpload;
